import React, { useState, useEffect, useMemo } from 'react';
import { formatISO, parseISO, format } from 'date-fns';
import {
  Alert,
  Autocomplete,
  Avatar,
  Box,
  Chip,
  CircularProgress,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  FormControlLabel,
  Grid,
  IconButton,
  Paper,
  Switch,
  TextField,
  Typography
} from '@mui/material';
import {
  AddCircleOutline as AddCircleOutlineIcon,
  Delete as DeleteIcon
} from '@mui/icons-material';
import AdminButton from 'components/common/AdminButton';
import { API, graphqlOperation } from 'aws-amplify';
import { managerService, roleService } from '../services/domain';
// REMOVED: Manager assignment mutations removed from schema (January 2025)
import { 
  updateOrgUnit, 
  createOrgUnitEmployee, 
  updateOrgUnitEmployee, 
  createHierarchyChangeLog 
} from 'graphql/mutations';
import { 
  employeesByClient, 
  orgUnitEmployeesByOrgUnit, 
  orgUnitsByClientAndLevel, 
  locationByClientId 
} from 'graphql/queries';
import { useUser } from 'components/RDAuthContext';

function EditOrgUnitModal({ open, onClose, unit, onSuccess, hierarchyLevels }) {
  const { user } = useUser();
  const [formData, setFormData] = useState({
    name: '',
    orgLevel: '',
    parentOrgUnitId: null,
    isActive: true,
    changeReason: ''
  });
  const [originalData, setOriginalData] = useState(null); // Track original data for change detection
  const [availableManagers, setAvailableManagers] = useState([]);
  const [availableRoles, setAvailableRoles] = useState([]);
  const [managerAssignments, setManagerAssignments] = useState([]);
  const [removedAssignments, setRemovedAssignments] = useState([]);
  const [removalReason, setRemovalReason] = useState('');
  const [availableParentUnits, setAvailableParentUnits] = useState([]);
  const [loading, setLoading] = useState(false);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState(null);
  const [childCount, setChildCount] = useState(0);
  const [loadingChildCount, setLoadingChildCount] = useState(false);

  // Check if form has changes compared to original
  const hasChanges = () => {
    if (!originalData) return false;
    
    // Check basic form data changes
    const basicChanges = (
      formData.name !== originalData.name ||
      formData.orgLevel !== originalData.orgLevel ||
      formData.parentOrgUnitId !== originalData.parentOrgUnitId ||
      formData.isActive !== originalData.isActive
    );
    
    // Check manager assignment changes
    const assignmentChanges = managerAssignments.some(a => !a.isExisting) || removedAssignments.length > 0;
    
    return basicChanges || assignmentChanges;
  };

  // Initialize form data when unit changes
  useEffect(() => {
    if (unit) {
      console.log('[EditOrgUnit] Initializing modal with unit:', unit);
      
      // Extract managerId properly - it could be in different fields
      const extractedManagerId = unit.managerId || unit.managerUserId || null;
      
      // Extract parentId - it could be in different fields
      const extractedParentId = unit.parentOrgUnitId || unit.parentId || null;

      console.log('[EditOrgUnit] Extracted IDs:', {
        managerId: extractedManagerId,
        parentId: extractedParentId,
        unitFields: {
          managerId: unit.managerId,
          managerUserId: unit.managerUserId,
          managerName: unit.managerName,
          manager: unit.manager
        }
      });

      const initialData = {
        name: unit.name || '',
        orgLevel: unit.orgLevel || unit.levelType || '',
        parentOrgUnitId: extractedParentId,
        isActive: unit.isActive !== false,
        changeReason: '' // Reset for new edit
      };
      
      console.log('[EditOrgUnit] Initial form data:', initialData);
      
      setFormData(initialData);
      setOriginalData(initialData); // Save original data for comparison
      
      // Reset child count
      setChildCount(0);
      
      // Reset assignments
      setManagerAssignments([]);
      setRemovedAssignments([]);
      setRemovalReason('');
      
      // Fetch current manager assignments
      fetchCurrentAssignments();
      
      // Fetch child count
      fetchChildCount();
    } else {
      // Reset states when modal closes
      setChildCount(0);
      setManagerAssignments([]);
      setRemovedAssignments([]);
    }
  }, [unit]);

  // Fetch available managers (employees) and roles
  useEffect(() => {
    const fetchAvailableData = async () => {
      if (!user?.client || !open || !formData.orgLevel) return;
      
      try {
        setLoading(true);
        
        // Fetch both employees and roles in parallel
        const [managers, roles] = await Promise.all([
          managerService.getAvailableManagers(user.client),
          roleService.getOrgUnitRoles(user.client, formData.orgLevel)
        ]);
        
        setAvailableManagers(managers);
        setAvailableRoles(roles);
      } catch (error) {
        console.error('Error fetching available data:', error);
        setError('Failed to load available managers or roles');
      } finally {
        setLoading(false);
      }
    };
    
    fetchAvailableData();
  }, [user, open, formData.orgLevel]);


  // Fetch available parent units based on org level
  useEffect(() => {
    const fetchParentUnits = async () => {
      if (!user?.client || !open || !formData.orgLevel) return;
      
      // Determine the parent level based on current level
      // In hierarchy: ORGLEVEL_3 is parent to ORGLEVEL_2, which is parent to ORGLEVEL_1
      // So numerically: lower number = child, higher number = parent
      const currentLevelNum = parseInt(formData.orgLevel.replace('ORGLEVEL_', ''));
      if (currentLevelNum <= 1) {
        // Level 1 is the lowest, needs a Level 2 parent
        const parentLevel = `ORGLEVEL_2`;
        
        try {
          const unitsResult = await API.graphql(
            graphqlOperation(orgUnitsByClientAndLevel, {
              clientId: user.client,
              orgLevel: { eq: parentLevel }
            })
          );
          
          const units = unitsResult?.data?.orgUnitsByClientAndLevel?.items || [];
          const parentUnits = units
            .filter(u => u.id !== unit?.id && u.isActive !== false) // Exclude self and inactive units
            .map(u => ({
              id: u.id,
              name: u.name,
              orgLevel: u.orgLevel
            }));

          setAvailableParentUnits(parentUnits);
        } catch (error) {
          console.error('Error fetching parent units:', error);
        }
      } else if (currentLevelNum === 2) {
        // Level 2 needs a Level 3 parent
        const parentLevel = `ORGLEVEL_3`;

        try {
          const unitsResult = await API.graphql(
            graphqlOperation(orgUnitsByClientAndLevel, {
              clientId: user.client,
              orgLevel: { eq: parentLevel }
            })
          );
          
          const units = unitsResult?.data?.orgUnitsByClientAndLevel?.items || [];
          const parentUnits = units
            .filter(u => u.id !== unit?.id && u.isActive !== false) // Exclude self and inactive units
            .map(u => ({
              id: u.id,
              name: u.name,
              orgLevel: u.orgLevel
            }));

          setAvailableParentUnits(parentUnits);
        } catch (error) {
          console.error('Error fetching parent units:', error);
        }
      } else {
        // Level 3 is the highest, no parent units
        setAvailableParentUnits([]);
      }
    };
    
    fetchParentUnits();
  }, [user, open, formData.orgLevel, unit]);

  // Fetch current manager assignments using OrgUnitEmployee
  const fetchCurrentAssignments = async () => {
    if (!unit?.id || !user?.client) return;
    
    try {
      const assignmentsResult = await API.graphql(
        graphqlOperation(orgUnitEmployeesByOrgUnit, {
          orgUnitId: unit.id,
          filter: { 
            isActive: { eq: true },
            clientId: { eq: user.client }
          },
          limit: 100 // Increased to get all active assignments
        })
      );
      
      const assignments = assignmentsResult?.data?.orgUnitEmployeesByOrgUnit?.items || [];
      console.log('[EditOrgUnit] Current active assignments found:', {
        count: assignments.length,
        assignments: assignments
      });
      
      // Convert assignments to our format
      if (assignments.length > 0) {
        const formattedAssignments = assignments.map(a => ({
          id: a.id,
          employee: {
            id: a.employeeId,
            firstName: a.employeeNameSnapshot?.split(' ')[0] || '',
            lastName: a.employeeNameSnapshot?.split(' ').slice(1).join(' ') || '',
            userName: a.employeeNameSnapshot || 'Unknown',
            email: ''
          },
          role: {
            id: a.roleDefinitionId || 'MANAGER',
            roleName: a.roleSnapshot || 'Manager'
          },
          isExisting: true,
          effectiveDate: a.effectiveDate,
          assignedBy: a.assignedBy,
          assignmentReason: a.assignmentReason
        }));
        setManagerAssignments(formattedAssignments);
      } else {
        // Start with one empty assignment row for new assignments
        setManagerAssignments([{ employee: null, role: null, isExisting: false }]);
      }
    } catch (error) {
      console.error('Error fetching current assignments:', error);
    }
  };

  // Fetch child count based on org level
  const fetchChildCount = async () => {
    if (!unit?.id || !user?.client) return;
    
    setLoadingChildCount(true);
    try {
      const orgLevel = unit.orgLevel || unit.levelType || '';
      let count = 0;
      
      if (orgLevel === 'ORGLEVEL_1') {
        // Count locations assigned to this OrgUnit
        const locationsResult = await API.graphql(
          graphqlOperation(locationByClientId, { 
            clientId: user.client,
            limit: 1000
          })
        );
        const locations = locationsResult?.data?.locationByClientId?.items || [];
        // Count locations where orgLevel_1 matches this unit's ID
        count = locations.filter(loc => loc.orgLevel_1 === unit.id).length;
        
      } else if (orgLevel === 'ORGLEVEL_2') {
        // Count OrgLevel_1 units that have this as parent
        const unitsResult = await API.graphql(
          graphqlOperation(orgUnitsByClientAndLevel, {
            clientId: user.client,
            orgLevel: { eq: 'ORGLEVEL_1' }
          })
        );
        const units = unitsResult?.data?.orgUnitsByClientAndLevel?.items || [];
        count = units.filter(u => u.parentOrgUnitId === unit.id || u.parentId === unit.id).length;
        
      } else if (orgLevel === 'ORGLEVEL_3') {
        // Count OrgLevel_2 units that have this as parent
        const unitsResult = await API.graphql(
          graphqlOperation(orgUnitsByClientAndLevel, {
            clientId: user.client,
            orgLevel: { eq: 'ORGLEVEL_2' }
          })
        );
        const units = unitsResult?.data?.orgUnitsByClientAndLevel?.items || [];
        count = units.filter(u => u.parentOrgUnitId === unit.id || u.parentId === unit.id).length;
      }
      
      setChildCount(count);
    } catch (error) {
      console.error('Error fetching child count:', error);
      setChildCount(0);
    } finally {
      setLoadingChildCount(false);
    }
  };

  const handleInputChange = (field, value) => {
    setFormData(prev => ({
      ...prev,
      [field]: value
    }));
  };

  const handleEmployeeChange = (index, newValue) => {
    const updated = [...managerAssignments];
    updated[index] = { ...updated[index], employee: newValue };
    setManagerAssignments(updated);
  };

  const handleRoleChange = (index, newValue) => {
    const updated = [...managerAssignments];
    updated[index] = { ...updated[index], role: newValue };
    setManagerAssignments(updated);
  };

  const handleAddAssignment = () => {
    setManagerAssignments([...managerAssignments, { employee: null, role: null, isExisting: false }]);
  };

  const handleRemoveAssignment = (index) => {
    const assignmentToRemove = managerAssignments[index];
    if (assignmentToRemove.isExisting) {
      // Track removed existing managers
      setRemovedAssignments([...removedAssignments, assignmentToRemove]);
    }
    setManagerAssignments(managerAssignments.filter((_, i) => i !== index));
  };

  const handleParentUnitChange = (event, newValue) => {
    setFormData(prev => ({
      ...prev,
      parentOrgUnitId: newValue?.id || null
    }));
  };

  const handleSave = async () => {
    if (!unit?.id) {
      console.error('No unit ID found:', unit);
      setError('Unable to save: Unit ID is missing');
      return;
    }
    
    console.log('[EditOrgUnit] Starting save with formData:', formData);
    console.log('[EditOrgUnit] Original data:', originalData);
    console.log('[EditOrgUnit] Manager assignments:', managerAssignments);
    
    setSaving(true);
    setError(null);
    
    try {
      // Track changes for HierarchyChangeLog
      const changeDetails = [];
      const fieldChanges = [];
      
      // Check what fields have changed
      if (unit.name !== formData.name) {
        fieldChanges.push({
          fieldName: 'name',
          oldValue: unit.name,
          oldDisplayValue: unit.name,
          newValue: formData.name,
          newDisplayValue: formData.name
        });
      }
      
      if ((unit.isActive !== false) !== formData.isActive) {
        fieldChanges.push({
          fieldName: 'isActive',
          oldValue: unit.isActive !== false,
          oldDisplayValue: unit.isActive !== false ? 'Active' : 'Inactive',
          newValue: formData.isActive,
          newDisplayValue: formData.isActive ? 'Active' : 'Inactive'
        });
      }
      
      const oldParentId = unit.parentId || unit.parentOrgUnitId || null;
      if (oldParentId !== formData.parentOrgUnitId) {
        // Find parent unit names for display
        const oldParent = availableParentUnits.find(u => u.id === oldParentId);
        const newParent = availableParentUnits.find(u => u.id === formData.parentOrgUnitId);
        
        fieldChanges.push({
          fieldName: 'parentOrgUnitId',
          oldValue: oldParentId,
          oldDisplayValue: oldParent?.name || 'None',
          newValue: formData.parentOrgUnitId,
          newDisplayValue: newParent?.name || 'None'
        });
      }
      
      // Check for manager assignment changes
      const hasNewAssignments = managerAssignments.some(a => !a.isExisting && a.employee && a.role);
      const hasRemovedAssignments = removedAssignments.length > 0;
      const managerChanged = hasNewAssignments || hasRemovedAssignments;
      
      console.log('[EditOrgUnit] Manager change detection:', {
        hasNewAssignments,
        hasRemovedAssignments,
        managerChanged,
        newAssignmentsCount: managerAssignments.filter(a => !a.isExisting).length,
        removedAssignmentsCount: removedAssignments.length
      });
                            
      if (managerChanged) {
        // Build display strings for old and new manager lists
        const existingManagers = managerAssignments.filter(a => a.isExisting);
        const allOldManagers = [...existingManagers, ...removedAssignments];
        const oldManagerNames = allOldManagers.map(a => 
          `${a.employee?.firstName} ${a.employee?.lastName}`.trim() || a.employee?.userName || 'Unknown'
        ).join(', ') || 'None';
        
        const currentManagers = managerAssignments.filter(a => a.employee && a.role);
        const newManagerNames = currentManagers.map(a => 
          `${a.employee?.firstName} ${a.employee?.lastName}`.trim() || a.employee?.userName || 'Unknown'
        ).join(', ') || 'None';
        
        fieldChanges.push({
          fieldName: 'managers',
          oldValue: allOldManagers.map(a => a.employee?.id).join(',') || '',
          oldDisplayValue: oldManagerNames,
          newValue: currentManagers.map(a => a.employee?.id).join(',') || '',
          newDisplayValue: newManagerNames
        });
      }
      
      // Only proceed if there are actual changes
      if (fieldChanges.length === 0) {
        console.log('[EditOrgUnit] No changes detected, closing modal');
        onClose();
        return;
      }
      
      console.log('[EditOrgUnit] Field changes detected:', fieldChanges);
      
      // Check if we have non-manager changes that require updating the OrgUnit
      const hasNonManagerChanges = fieldChanges.some(fc => 
        fc.fieldName !== 'manager'
      );
      
      // Only update OrgUnit if there are non-manager field changes
      if (hasNonManagerChanges) {
        const updateInput = {
          id: unit.id,
          name: formData.name,
          isActive: formData.isActive,
          parentOrgUnitId: formData.parentOrgUnitId
        };
        
        // Log the input to debug

        const updateResult = await API.graphql(
          graphqlOperation(updateOrgUnit, { input: updateInput })
        );

      } else {
      }
      
      // Handle manager assignment if changed
      if (managerChanged) {
        console.log('[EditOrgUnit] Processing manager change...');
        
        // Deactivate removed assignments
        const existingAssignments = managerAssignments.filter(a => a.isExisting);
        if (removedAssignments && removedAssignments.length > 0) {
          console.log('[EditOrgUnit] Deactivating removed assignments:', removedAssignments.length);
          
          const deactivatePromises = removedAssignments.map(async (assignment) => {
            const updateInput = {
              id: assignment.id,
              endDate: formatISO(new Date()),
              isActive: false,
              changeReason: formData.changeReason || 'Manager reassignment',
              lastModified: formatISO(new Date()),
              changedBy: user.id
            };
            
            console.log('[EditOrgUnit] Deactivating assignment:', assignment.id, 'for employee:', assignment.employeeId);
            console.log('[EditOrgUnit] updateOrgUnitEmployee input:', updateInput);
            
            try {
              const updateResult = await API.graphql(
                graphqlOperation(updateOrgUnitEmployee, { input: updateInput })
              );
              console.log('[EditOrgUnit] Successfully deactivated assignment:', assignment.id);
              return updateResult;
            } catch (error) {
              console.error('[EditOrgUnit] Failed to deactivate assignment:', assignment.id, error);
              // Continue with other deactivations even if one fails
              return null;
            }
          });
          
          // Wait for all deactivations to complete
          const results = await Promise.all(deactivatePromises);
          console.log('[EditOrgUnit] All deactivations completed:', results.filter(r => r !== null).length, 'succeeded');
        } else {
          console.log('[EditOrgUnit] No current assignments to deactivate');
        }
        
        // Create new assignments
        const newAssignments = managerAssignments.filter(a => !a.isExisting && a.employee && a.role);
        if (newAssignments.length > 0) {
          console.log('[EditOrgUnit] Creating new assignments:', newAssignments.length);
          
          const now = formatISO(new Date());
          
          const createPromises = newAssignments.map(async (assignment) => {
            const newAssignmentInput = {
              clientId: user.client,
              employeeId: assignment.employee.id, // This is the Employee.id
              orgUnitId: unit.id,
              managementLevel: formData.orgLevel,
              roleDefinitionId: assignment.role.id,
              isActive: true,
              effectiveDate: now,
              assignedBy: user.id,
              assignmentReason: formData.changeReason || 'Manual assignment via Location Admin',
              employeeNameSnapshot: `${assignment.employee.firstName} ${assignment.employee.lastName}`.trim() || 'Unknown',
              orgUnitNameSnapshot: formData.name, // Use the current name of the org unit
              roleSnapshot: assignment.role.roleName,
              createdAt: now,
              lastModified: now,
              changedBy: user.id
            };
            
            console.log('[EditOrgUnit] createOrgUnitEmployee input:', newAssignmentInput);

            try {
              const result = await API.graphql(
                graphqlOperation(createOrgUnitEmployee, { input: newAssignmentInput })
              );
              
              console.log('[EditOrgUnit] createOrgUnitEmployee result:', result.data);
              return result.data;
            } catch (error) {
              console.error('[EditOrgUnit] Error creating manager assignment:', error);
              console.error('[EditOrgUnit] Error details:', {
                message: error.message,
                errors: error.errors,
                data: error.data
              });
              throw error; // Re-throw to be caught by the outer try-catch
            }
          });
          
          await Promise.all(createPromises);
        } else {
          console.log('[EditOrgUnit] No new assignments to create');
        }
      }
      
      // The title should always be "OrgUnit Updated" since we're in the Units tab
      const changeSummary = `OrgUnit Updated: ${formData.name}`;
      
      // Determine changeType based on what changed
      let changeType = '';
      if (hasNonManagerChanges && managerChanged) {
        changeType = 'OrgUnit,ManagerAssignment'; // Both changed - comma separated for multiple chips
      } else if (managerChanged) {
        changeType = 'ManagerAssignment'; // Only manager changed
      } else {
        changeType = 'OrgUnit'; // Only org unit fields changed
      }
      
      // Create HierarchyChangeLog entry
      const changeLog = {
        clientId: user.client,
        changeType,
        changeSummary,
        changeDetails: [{
          entityType: 'OrgUnit',
          entityId: unit.id,
          entityName: formData.name,
          operationType: 'UPDATE',
          fieldChanges: fieldChanges,
          metadata: JSON.stringify({
            orgLevel: formData.orgLevel,
            editedVia: 'Location Admin',
            userProvidedReason: formData.changeReason || null
          })
        }],
        affectedLocations: [], // Required field, empty array for org unit changes
        affectedOrgUnits: [unit.id],
        changedBy: user.id,
        changedByName: `${user.firstName || ''} ${user.lastName || ''}`.trim() || user.email || user.userName,
        changedAt: formatISO(new Date()),
        changeReason: formData.changeReason || 'Manual update via Location Admin interface'
      };

      await API.graphql(
        graphqlOperation(createHierarchyChangeLog, { input: changeLog })
      );
      
      console.log('[EditOrgUnit] Save completed successfully');
      console.log('[EditOrgUnit] Change summary:', changeSummary);
      console.log('[EditOrgUnit] Manager change was:', managerChanged ? 'YES' : 'NO');
      
      // Success
      if (onSuccess) {
        onSuccess();
      }
      onClose();
    } catch (error) {
      console.error('[EditOrgUnit] Error saving org unit:', error);
      console.error('[EditOrgUnit] Full error object:', {
        message: error.message,
        errors: error.errors,
        data: error.data,
        stack: error.stack
      });
      setError('Failed to save changes. Please try again.');
    } finally {
      setSaving(false);
    }
  };

  
  // Find selected parent unit - compare as strings to avoid type mismatch
  // Use useMemo for consistency
  const selectedParentUnit = useMemo(() => {
    if (!formData.parentOrgUnitId || availableParentUnits.length === 0) {
      return null;
    }
    return availableParentUnits.find(u => String(u.id) === String(formData.parentOrgUnitId)) || null;
  }, [formData.parentOrgUnitId, availableParentUnits]);
  
  // Debug logging for parent unit selection
  if (formData.parentOrgUnitId && availableParentUnits.length > 0) {
  }

  // Get the level label for display
  const getLevelLabel = () => {
    if (!formData.orgLevel) return '';
    const level = hierarchyLevels?.find(l => l.levelId === formData.orgLevel);
    return level?.label || formData.orgLevel;
  };

  // Get child type label based on org level
  const getChildTypeLabel = () => {
    const orgLevel = formData.orgLevel;
    if (orgLevel === 'ORGLEVEL_1') return 'locations';
    if (orgLevel === 'ORGLEVEL_2') {
      const level1 = hierarchyLevels?.find(l => l.levelId === 'ORGLEVEL_1');
      const label = level1?.label || 'Level 1 Units';
      // Make it lowercase and plural if needed
      return label.toLowerCase().endsWith('s') ? label.toLowerCase() : `${label.toLowerCase()}s`;
    }
    if (orgLevel === 'ORGLEVEL_3') {
      const level2 = hierarchyLevels?.find(l => l.levelId === 'ORGLEVEL_2');
      const label = level2?.label || 'Level 2 Units';
      // Make it lowercase and plural if needed
      return label.toLowerCase().endsWith('s') ? label.toLowerCase() : `${label.toLowerCase()}s`;
    }
    return 'child units';
  };

  // Check if deactivation is allowed
  const canDeactivate = childCount === 0;

  return (
    <Dialog
      open={open}
      onClose={onClose}
      maxWidth="sm"
      fullWidth
      PaperProps={{
        sx: {
          borderRadius: 2,
          boxShadow: '0 8px 32px rgba(0, 0, 0, 0.08)'
        }
      }}
    >
      <DialogTitle>
        Edit Organizational Unit
      </DialogTitle>
      
      <DialogContent>
        <Box pt={2}>
          {error && (
            <Alert severity="error" sx={{ mb: 2 }} onClose={() => setError(null)}>
              {error}
            </Alert>
          )}
          
          {/* Unit ID as text */}
          <Box mb={2}>
            <Typography variant="caption" color="text" fontWeight="medium">
              Unit ID
            </Typography>
            <Typography variant="body2" color="text">
              {unit?.id || ''}
            </Typography>
          </Box>
          
          {/* Unit Name */}
          <TextField
            fullWidth
            label="Unit Name"
            value={formData.name}
            onChange={(e) => handleInputChange('name', e.target.value)}
            margin="normal"
            required
            variant="outlined"
          />
          
          {/* Org Level (Read-only) */}
          <TextField
            fullWidth
            label="Organizational Level"
            value={getLevelLabel()}
            margin="normal"
            disabled
            variant="outlined"
            helperText="Organizational level cannot be changed"
          />
          
          {/* Parent Unit */}
          {availableParentUnits.length > 0 && (
            <Autocomplete
              options={availableParentUnits}
              value={selectedParentUnit}
              onChange={handleParentUnitChange}
              getOptionLabel={(option) => option.name}
              getOptionKey={(option) => option.id} // Use ID as key instead of name
              isOptionEqualToValue={(option, value) => {
                // Compare as strings to handle potential type mismatches
                return String(option.id) === String(value?.id);
              }}
              renderInput={(params) => (
                <TextField
                  {...params}
                  label="Parent Unit"
                  margin="normal"
                  variant="outlined"
                  helperText="Select the parent unit in the hierarchy"
                />
              )}
              loading={loading}
              loadingText="Loading parent units..."
              noOptionsText="No parent units available"
            />
          )}
          
          {/* Manager Assignments Section */}
          <Box sx={{ mt: 2, mb: 2 }}>
            <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 1 }}>
              <Typography variant="subtitle2" fontWeight="600">
                Manager Assignments
              </Typography>
              <AdminButton
                variant="outlined"
                onClick={handleAddAssignment}
                disabled={saving}
                startIcon={<AddCircleOutlineIcon />}
                size="small"
              >
                Add Assignment
              </AdminButton>
            </Box>

            {/* Assignment Rows */}
            {managerAssignments.map((assignment, index) => (
              <Paper key={index} variant="outlined" sx={{ p: 2, mb: 1 }}>
                <Grid container spacing={2} alignItems="center">
                  <Grid item xs={5}>
                    <Autocomplete
                      value={assignment.employee}
                      options={availableManagers.filter(u => 
                        !managerAssignments.some((a, i) => i !== index && a.employee?.id === u.id)
                      )}
                      getOptionLabel={(option) => 
                        `${option.firstName} ${option.lastName}`
                      }
                      renderOption={(props, option) => (
                        <Box component="li" {...props}>
                          <Avatar sx={{ mr: 2, width: 32, height: 32 }}>
                            {option.firstName?.charAt(0) || 'E'}
                          </Avatar>
                          <Box>
                            <Typography variant="body2">
                              {option.firstName} {option.lastName}
                            </Typography>
                            <Typography variant="caption" color="text.secondary">
                              {option.email}
                            </Typography>
                          </Box>
                        </Box>
                      )}
                      renderInput={(params) => (
                        <TextField
                          {...params}
                          label="Employee"
                          placeholder="Select employee..."
                          variant="outlined"
                          size="small"
                        />
                      )}
                      onChange={(event, newValue) => handleEmployeeChange(index, newValue)}
                      loading={loading}
                      disabled={saving || assignment.isExisting}
                      fullWidth
                    />
                  </Grid>
                  <Grid item xs={5}>
                    <Autocomplete
                      value={assignment.role}
                      options={availableRoles}
                      getOptionLabel={(option) => option.roleName || ''}
                      renderInput={(params) => (
                        <TextField
                          {...params}
                          label="Role"
                          placeholder="Select role..."
                          variant="outlined"
                          size="small"
                        />
                      )}
                      onChange={(event, newValue) => handleRoleChange(index, newValue)}
                      loading={loading}
                      disabled={saving || assignment.isExisting}
                      fullWidth
                    />
                  </Grid>
                  <Grid item xs={2}>
                    <Box sx={{ display: 'flex', justifyContent: 'flex-end', alignItems: 'center', gap: 1 }}>
                      {assignment.isExisting && (
                        <Chip 
                          label="Current" 
                          size="small"
                          color="primary"
                          variant="outlined"
                        />
                      )}
                      <IconButton 
                        onClick={() => handleRemoveAssignment(index)}
                        disabled={saving}
                        size="small"
                        color="error"
                      >
                        <DeleteIcon fontSize="small" />
                      </IconButton>
                    </Box>
                  </Grid>
                  {assignment.effectiveDate && (
                    <Grid item xs={12}>
                      <Typography variant="caption" color="text.secondary">
                        Effective: {format(parseISO(assignment.effectiveDate), 'MMM d, yyyy')}
                      </Typography>
                    </Grid>
                  )}
                </Grid>
              </Paper>
            ))}

            {managerAssignments.length === 0 && (
              <Box 
                sx={{ 
                  p: 3, 
                  textAlign: 'center', 
                  border: '1px dashed',
                  borderColor: 'divider',
                  borderRadius: 1,
                  backgroundColor: 'action.hover'
                }}
              >
                <Typography variant="body2" color="text.secondary">
                  No managers assigned. Click "Add Assignment" to assign managers.
                </Typography>
              </Box>
            )}

            {/* Reason for Removals */}
            {removedAssignments.length > 0 && (
              <Alert severity="warning" sx={{ mt: 2 }}>
                <Typography variant="body2" fontWeight="600" gutterBottom>
                  Removing {removedAssignments.length} manager(s)
                </Typography>
                <TextField
                  fullWidth
                  label="Reason for Removal"
                  value={removalReason}
                  onChange={(e) => setRemovalReason(e.target.value)}
                  placeholder="e.g., Role change, Termination, Reassignment..."
                  variant="outlined"
                  size="small"
                  required
                  disabled={saving}
                  sx={{ mt: 1 }}
                />
              </Alert>
            )}
          </Box>
          
          {/* Active Status */}
          <Box mt={2} mb={1}>
            <FormControlLabel
              control={
                <Switch
                  checked={formData.isActive}
                  onChange={(e) => handleInputChange('isActive', e.target.checked)}
                  color="success"
                  disabled={formData.isActive && !canDeactivate}
                />
              }
              label={
                <Box>
                  <Typography variant="body2">
                    {formData.isActive ? 'Active' : 'Inactive'}
                  </Typography>
                  {!canDeactivate && formData.isActive && (
                    <Typography variant="caption" color="text">
                      Cannot deactivate - {childCount} {getChildTypeLabel()} assigned
                    </Typography>
                  )}
                </Box>
              }
            />
          </Box>
          
          {!formData.isActive && (
            <Alert severity="warning" sx={{ mt: 1 }}>
              This unit is currently inactive and not part of the active hierarchy.
            </Alert>
          )}
          
          {/* Change Reason */}
          <TextField
            fullWidth
            label="Change Reason"
            value={formData.changeReason}
            onChange={(e) => handleInputChange('changeReason', e.target.value)}
            margin="normal"
            variant="outlined"
            multiline
            rows={3}
            required={hasChanges()}
            error={hasChanges() && !formData.changeReason.trim()}
            inputProps={{ maxLength: 500 }}
            helperText={
              hasChanges() 
                ? `Required: Document why this change is being made (${formData.changeReason.length}/500 characters)`
                : `Make changes above to enable saving (${formData.changeReason.length}/500 characters)`
            }
          />
        </Box>
      </DialogContent>
      
      <DialogActions sx={{ p: 2.5, pt: 1 }}>
        <AdminButton
          onClick={onClose}
          variant="outlined"
          disabled={saving}
        >
          Cancel
        </AdminButton>
        <AdminButton
          onClick={handleSave}
          variant="contained"
          disabled={
            saving || 
            !formData.name || 
            !hasChanges() || 
            !formData.changeReason.trim() ||
            // Check for incomplete assignments (missing employee or role)
            managerAssignments.some(a => !a.employee || !a.role) ||
            // Check for missing reason for removals
            (removedAssignments.length > 0 && !removalReason.trim())
          }
        >
          {saving ? <CircularProgress size={20} color="inherit" /> : 'Save Changes'}
        </AdminButton>
      </DialogActions>
    </Dialog>
  );
}

export default EditOrgUnitModal;